import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * EN-11109: the Sit & Go end-of-game result and its rematch offer, as APP
 * state. The modal and the felt's Go to lobby chip are only views of an entry
 * here.
 *
 * The offer has no clock: it stays open until the player chooses or leaves
 * the table. A timed offer comes back with seat locking (EN-11139 AC 10), once
 * the next table's seat can be held for the player while it runs.
 *
 * Kept out of the table panel on purpose: TournamentScreenModal unmounts with
 * its panel, and the backend deletes tournament_stats after a grace period, so
 * position / prize come through null after that. The outcome is snapshotted
 * when it first arrives and never read from the feed again (except to pick up
 * a payout that lands on a later push — see startSitGoResult).
 *
 * Keyed by the table's gameID. Only written while release.sit-go-result-modal
 * is on (SitGoResultWatcher), so with the flag off this slice stays empty.
 */

export type SitGoOfferStatus = 'pending' | 'accepted';

/** The lobby row the rematch registers into: the engine's next-table key. */
export type SitGoTemplate = {
  tournamentId: string;
  title: string;
  game: string;
  buyIn: number;
  entryFee: number;
  minPlayers: number;
  maxPlayers: number;
  startingChips: number;
  schedule: string;
  maxSeat: number;
};

export type SitGoOutcome = {
  position: number | null;
  prize: number;
  /** Seats at the table: 2 heads-up, 6 six-seat. 0 when unknown. */
  capacity: number;
  title: string;
};

export type SitGoResultEntry = {
  gameID: string;
  tournamentId: string;
  /** The table's game type ('texas' / 'omaha'), for re-opening it. */
  game: string;
  /**
   * When the result arrived. Tells a restored entry from a newer one, and
   * bounds how old a stored entry may be to come back after a reload.
   */
  startedAtMs: number;
  outcome: SitGoOutcome;
  template: SitGoTemplate | null;
  /** The tournament itself has finished — not just this player's part in it. */
  tableFinished: boolean;
  modalOpen: boolean;
  /** Yes was pressed: the Confirm Registration screen is up for this offer. */
  registrationOpen: boolean;
  status: SitGoOfferStatus;
  /**
   * EN-11310: the rematch registered and the next table is opening; this
   * finished table is about to be left. Its felt shows nothing meanwhile.
   */
  rematchRegistered?: boolean;
};

export type SitGoResultState = {
  byGameId: Record<string, SitGoResultEntry>;
  /** Lobby rows captured while the Sit & Go was still listed (it drops at 'F'). */
  templatesByTournamentId: Record<string, SitGoTemplate>;
};

const initialState: SitGoResultState = {
  byGameId: {},
  templatesByTournamentId: {}
};

export type StartSitGoResultPayload = {
  gameID: string;
  tournamentId: string;
  game: string;
  outcome: SitGoOutcome;
  tableFinished: boolean;
  startedAtMs: number;
};

export const isSitGoWin = (outcome: SitGoOutcome): boolean => outcome.prize > 0;

const sitGoResultSlice = createSlice({
  name: 'sitGoResult',
  initialState,
  reducers: {
    rememberSitGoTemplate: (state, action: PayloadAction<SitGoTemplate>) => {
      state.templatesByTournamentId[action.payload.tournamentId] = action.payload;
      // A result that arrived before its lobby row did picks the row up now.
      Object.values(state.byGameId).forEach((entry) => {
        if (!entry.template && entry.tournamentId === action.payload.tournamentId) {
          entry.template = action.payload;
        }
      });
    },

    /**
     * Start the offer the first time this player's result arrives for a table.
     * Idempotent: a repeat push for the same tournament never restarts the
     * offer or reopens a dismissed modal. It can only ADD information — a
     * payout that a later push carries (the payout pass can land after the
     * bust), or the tournament finishing — never take it away, because the
     * feed's fields go null once tournament_stats is cleaned up.
     */
    startSitGoResult: (state, action: PayloadAction<StartSitGoResultPayload>) => {
      const { gameID, tournamentId, game, outcome, tableFinished, startedAtMs } = action.payload;
      const existing = state.byGameId[gameID];
      if (existing && existing.tournamentId === tournamentId) {
        if (outcome.prize > existing.outcome.prize) {
          existing.outcome.prize = outcome.prize;
          if (outcome.position) existing.outcome.position = outcome.position;
        }
        if (!existing.outcome.capacity && outcome.capacity) {
          existing.outcome.capacity = outcome.capacity;
        }
        if (tableFinished) existing.tableFinished = true;
        return;
      }
      state.byGameId[gameID] = {
        gameID,
        tournamentId,
        game,
        startedAtMs,
        outcome,
        template: state.templatesByTournamentId[tournamentId] ?? null,
        tableFinished,
        modalOpen: true,
        registrationOpen: false,
        status: 'pending'
      };
    },

    /**
     * A page reload: put back the offer the player had, with its modal state
     * and status. SitGoResultWatcher dispatches this only once the entry's
     * table is back in state.tables.
     *
     *  - Confirm Registration was up: it does not survive a reload, so this is
     *    its Cancel: back to the result modal.
     *  - A status this build does not know (one stored by a build that still
     *    had the countdown) is an open offer again.
     *  - Detection got there first (a busted player at a table still in play,
     *    whose row re-arrives): the older start time is the original offer, so
     *    the stored entry wins, keeping anything the newer push added.
     */
    restoreSitGoResult: (
      state,
      action: PayloadAction<{
        entry: SitGoResultEntry;
        template: SitGoTemplate | null;
      }>
    ) => {
      const { entry: stored, template } = action.payload;
      const tournamentTemplate = stored.template ?? template;
      if (tournamentTemplate && !state.templatesByTournamentId[stored.tournamentId]) {
        state.templatesByTournamentId[stored.tournamentId] = tournamentTemplate;
      }
      const existing = state.byGameId[stored.gameID];
      if (
        existing &&
        existing.tournamentId === stored.tournamentId &&
        existing.startedAtMs <= stored.startedAtMs
      ) {
        return;
      }
      const restored: SitGoResultEntry = {
        ...stored,
        outcome: { ...stored.outcome },
        template: tournamentTemplate ?? state.templatesByTournamentId[stored.tournamentId] ?? null
      };
      if (existing && existing.tournamentId === stored.tournamentId) {
        if (existing.outcome.prize > restored.outcome.prize) {
          restored.outcome.prize = existing.outcome.prize;
          if (existing.outcome.position) restored.outcome.position = existing.outcome.position;
        }
        if (!restored.outcome.capacity) restored.outcome.capacity = existing.outcome.capacity;
        if (existing.tableFinished) restored.tableFinished = true;
      }
      // The table is back after a reload, so it was not left after all.
      restored.rematchRegistered = false;
      if (restored.registrationOpen) {
        restored.registrationOpen = false;
        restored.status = 'pending';
        restored.modalOpen = true;
      } else if (restored.status !== 'pending' && restored.status !== 'accepted') {
        restored.status = 'pending';
      }
      state.byGameId[stored.gameID] = restored;
    },

    /** The close X or backdrop: closes the modal only. The offer stays open. */
    closeSitGoResultModal: (state, action: PayloadAction<string>) => {
      const entry = state.byGameId[action.payload];
      if (entry) entry.modalOpen = false;
    },

    /** Yes: the modal closes and Confirm Registration opens. */
    acceptSitGoRematch: (state, action: PayloadAction<string>) => {
      const entry = state.byGameId[action.payload];
      if (!entry || entry.status !== 'pending') return;
      entry.status = 'accepted';
      entry.modalOpen = false;
      entry.registrationOpen = true;
    },

    /**
     * Cancel on Confirm Registration / Registration Failed: back to the result
     * modal, the offer still open.
     */
    cancelSitGoRegistration: (
      state,
      action: PayloadAction<{ gameID: string; reopen?: boolean }>
    ) => {
      const entry = state.byGameId[action.payload.gameID];
      if (!entry) return;
      entry.registrationOpen = false;
      entry.status = 'pending';
      // reopen: false — the registration left for somewhere else (Cashier,
      // the lobby): the modal stays closed, and the felt shows only its Go to
      // lobby chip (no way back to the offer until EN-11303).
      entry.modalOpen = action.payload.reopen !== false;
    },

    /** Registered into the next table, which is opening: the finished one follows it out. */
    sitGoRematchRegistered: (state, action: PayloadAction<string>) => {
      const entry = state.byGameId[action.payload];
      if (!entry) return;
      entry.registrationOpen = false;
      entry.modalOpen = false;
      entry.status = 'accepted';
      entry.rematchRegistered = true;
    },

    /** No / Go to lobby / Exit Table, or the table went away: the offer goes with it. */
    clearSitGoResult: (state, action: PayloadAction<string>) => {
      delete state.byGameId[action.payload];
    }
  }
});

export const {
  rememberSitGoTemplate,
  startSitGoResult,
  restoreSitGoResult,
  closeSitGoResultModal,
  acceptSitGoRematch,
  cancelSitGoRegistration,
  sitGoRematchRegistered,
  clearSitGoResult
} = sitGoResultSlice.actions;

export { sitGoResultSlice };
export default sitGoResultSlice.reducer;
